import { Dispatch, SetStateAction, useCallback } from "react";
import { UseFormSetValue } from "react-hook-form";
import { CollectorDTO } from "api";
import { UtilityBillFormData } from "types";
import { getCollectorString } from "pages/authenticated/documents/utils";

interface UseSiteHandlersParams {
  isCreateMode: boolean;
  currentSite: { id: number | undefined; name: string | null };
  setCurrentSite: Dispatch<SetStateAction<{ id: number | undefined; name: string | null }>>;
  setValue: UseFormSetValue<UtilityBillFormData>;
  setNewCollectorId: Dispatch<SetStateAction<number | undefined>>;
  setIsCollectorEmpty: Dispatch<SetStateAction<boolean>>;
  setOpenAddSiteDialog: Dispatch<SetStateAction<boolean>>;
}

export const useSiteHandlers = ({
  isCreateMode,
  currentSite,
  setCurrentSite,
  setValue,
  setNewCollectorId,
  setIsCollectorEmpty,
  setOpenAddSiteDialog,
}: UseSiteHandlersParams) => {
  const handleOtherSitesButtonClick = () => {
    setOpenAddSiteDialog(true);
  };

  const handleApplyNewSite = (newSiteApplied: { id: number | undefined; name: string }) => {
    if (newSiteApplied.id === currentSite.id) {
      return;
    }

    setCurrentSite({ id: newSiteApplied.id, name: newSiteApplied.name || null });
    if (isCreateMode) {
      setValue("siteName", newSiteApplied.name || "");
    }

    setNewCollectorId(undefined);
    const emptyCollectorString = getCollectorString({
      accountNumber: "-",
      utilityTypeName: "-",
      meterNumber: "-",
    } as CollectorDTO);
    setValue("collector", emptyCollectorString);
    setIsCollectorEmpty(true);
    setValue("siteName", newSiteApplied.name, { shouldDirty: true });
    setValue("accountNumber", "", { shouldDirty: true });
    setValue("meterNumber", "", { shouldDirty: true });
    setValue("utilityTypeName", "", { shouldDirty: true });
    setValue("provider", "", { shouldDirty: true });
    setValue("subtype", "", { shouldDirty: true });
  };

  // Wrapper function to match the expected type signature for child components
  const handleSetNewSite: Dispatch<SetStateAction<{ id: number | undefined; name: string }>> = useCallback((site) => {
    const siteValue = typeof site === 'function' ? site({ id: undefined, name: "" }) : site;
    setCurrentSite({ id: siteValue.id, name: siteValue.name || null });
    if (isCreateMode) {
      setValue("siteName", siteValue.name || "");
    }
  }, [isCreateMode, setValue, setCurrentSite]);

  return {
    handleOtherSitesButtonClick,
    handleApplyNewSite,
    handleSetNewSite,
  };
};

